import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPlatformClass } from '../../utils/platformUtils';
import { useStockMovements } from './hooks/useStockMovements';
import { useProducts } from './hooks/useProducts';
import PlatformText from '../PlatformText';
import PlatformButton from '../PlatformButton';
import InventoryBackButton from './InventoryBackButton';
import InventoryLayout from './InventoryLayout';
import './TransactionsPage.css';

const TransactionsPage = () => {
  const navigate = useNavigate();
  const platformClass = getPlatformClass();
  const isMobilePlatform = platformClass.includes('ios') || platformClass.includes('android') || platformClass.includes('pwa');

  // Hooks
  const { stockMovements, loading: movementsLoading, error: movementsError } = useStockMovements();
  const { products, loading: productsLoading } = useProducts();

  // Local state
  const [enrichedMovements, setEnrichedMovements] = useState([]);
  const [filteredMovements, setFilteredMovements] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('all');
  const [sortField, setSortField] = useState('timestamp');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [transactionsPerPage] = useState(25); // Show 25 transactions per page

  // Loading and error states
  const loading = movementsLoading || productsLoading;
  const error = movementsError;

  // Filter and sort movements
  const applyFiltersAndSorting = useCallback((movementsToFilter) => {
    let filtered = [...movementsToFilter];

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(movement => 
        movement.challanNumber?.toLowerCase().includes(searchLower) ||
        movement.partyName?.toLowerCase().includes(searchLower) ||
        movement.productName?.toLowerCase().includes(searchLower) ||
        movement.variantName?.toLowerCase().includes(searchLower) ||
        movement.sku?.toLowerCase().includes(searchLower)
      );
    }

    // Apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(movement => movement.type === typeFilter);
    }

    // Apply product filter
    if (productFilter !== 'all') {
      filtered = filtered.filter(movement => movement.productId === productFilter);
    }

    // Apply sorting (only by timestamp)
    if (sortField === 'timestamp') {
      filtered.sort((a, b) => {
        const aValue = a.timestamp instanceof Date ? a.timestamp : new Date(a.timestamp);
        const bValue = b.timestamp instanceof Date ? b.timestamp : new Date(b.timestamp);

        if (sortDirection === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    }

    // Update filtered movements without triggering infinite loop
    setFilteredMovements(filtered);
    // Reset to first page when filters change
    setCurrentPage(1);
  }, [searchTerm, typeFilter, productFilter, sortField, sortDirection]);

  // Process and enrich movements data when it changes
  useEffect(() => {
    if (stockMovements.length > 0 && products.length > 0) {
      const enriched = stockMovements.map(movement => {
        const product = products.find(p => p.id === movement.productId);
        if (product) {
          return {
            ...movement,
            product,
            sku: movement.variantId && product.hasVariants 
              ? product.variants?.find(v => v.id === movement.variantId)?.sku || product.code
              : product.code
          };
        }
        return movement;
      });

      // Store enriched movements for filtering
      setEnrichedMovements(enriched);
    } else {
      setEnrichedMovements(stockMovements);
    }
  }, [stockMovements, products]);

  // Apply filters and sorting when filter states change
  useEffect(() => {
    if (enrichedMovements.length > 0) {
      applyFiltersAndSorting(enrichedMovements);
    }
  }, [searchTerm, typeFilter, productFilter, sortField, sortDirection, enrichedMovements, applyFiltersAndSorting]);

  // Handle sorting (only for timestamp/date)
  const handleSort = (field) => {
    if (field === 'timestamp') {
      if (sortField === field) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortField(field);
        setSortDirection('asc');
      }
    }
  };

  // Get movement type display
  const getMovementTypeDisplay = (type) => {
    const typeMap = {
      'stock_in': 'Stock In',
      'stock_out': 'Stock Out',
      'adjustment': 'Adjustment'
    };
    return typeMap[type] || type;
  };

  // Get movement type color
  const getMovementTypeColor = (type) => {
    const colorMap = {
      'stock_in': '#10b981', // Green
      'stock_out': '#ef4444', // Red
      'adjustment': '#f59e0b' // Yellow
    };
    return colorMap[type] || '#6b7280';
  };

  // Get product name for display
  const getProductName = (productId) => {
    if (!products || products.length === 0) return 'Unknown Product';
    const product = products.find(p => p.id === productId);
    return product ? product.name : 'Unknown Product';
  };

  // Pagination calculations
  const totalPages = Math.ceil((filteredMovements?.length || 0) / transactionsPerPage);
  const startIndex = (currentPage - 1) * transactionsPerPage;
  const endIndex = startIndex + transactionsPerPage;
  const currentTransactions = (filteredMovements || []).slice(startIndex, endIndex);

  // Handle page navigation
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Scroll to top of table
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle next/previous page
  const handleNextPage = () => {
    if (currentPage < totalPages && totalPages > 0) {
      handlePageChange(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1 && totalPages > 0) {
      handlePageChange(currentPage - 1);
    }
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Render desktop table
  const renderDesktopTable = () => (
    <div className="transactions-table-container">
      <table className="transactions-table">
        <thead>
          <tr>
            <th onClick={() => handleSort('timestamp')} className="sortable-header">
              Date
              {sortField === 'timestamp' && (
                <span className="sort-indicator">
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </th>
            <th>Challan Number</th>
            <th>Customer Name</th>
            <th>Product</th>
            <th>Variant</th>
            <th>SKU</th>
            <th>Type</th>
            <th>Quantity</th>
            <th>Previous Stock</th>
            <th>New Stock</th>
            <th>Reason</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {currentTransactions.map((movement) => (
            <tr key={movement.id} className="movement-row">
              <td className="date-cell">
                <PlatformText variant="caption" className="date-text">
                  {formatDate(movement.timestamp)}
                </PlatformText>
              </td>
              <td className="challan-cell">
                <PlatformText variant="body" className="challan-text">
                  {movement.challanNumber || (movement.type === 'stock_in' ? 'N/A' : 'Required')}
                </PlatformText>
              </td>
              <td className="customer-cell">
                <PlatformText variant="body" className="customer-text">
                  {movement.partyName || (movement.type === 'stock_in' ? 'N/A' : 'Required')}
                </PlatformText>
              </td>
              <td className="product-cell">
                <PlatformText variant="body" className="product-name">
                  {movement.productName}
                </PlatformText>
                {movement.product?.category && (
                  <PlatformText variant="caption" className="product-category">
                    {movement.product.category}
                  </PlatformText>
                )}
              </td>
              <td className="variant-cell">
                {movement.variantName ? (
                  <PlatformText variant="body" className="variant-name">
                    {movement.variantName}
                  </PlatformText>
                ) : (
                  <PlatformText variant="caption" className="no-variant">
                    No Variant
                  </PlatformText>
                )}
              </td>
              <td className="sku-cell">
                <PlatformText variant="body" className="sku-text">
                  {movement.sku || 'N/A'}
                </PlatformText>
              </td>
              <td className="type-cell">
                <span 
                  className="movement-type-badge"
                  style={{ backgroundColor: getMovementTypeColor(movement.type) }}
                >
                  {getMovementTypeDisplay(movement.type)}
                </span>
              </td>
              <td className="quantity-cell">
                <PlatformText variant="body" className="quantity-text">
                  {movement.quantity}
                </PlatformText>
              </td>
              <td className="stock-cell">
                <PlatformText variant="body" className="stock-text">
                  {movement.previousStock}
                </PlatformText>
              </td>
              <td className="stock-cell">
                <PlatformText variant="body" className="stock-text">
                  {movement.newStock}
                </PlatformText>
              </td>
              <td className="reason-cell">
                <PlatformText variant="body" className="reason-text">
                  {movement.reason || 'N/A'}
                </PlatformText>
              </td>
              <td className="notes-cell">
                <PlatformText variant="caption" className="notes-text">
                  {movement.notes || 'N/A'}
                </PlatformText>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Render mobile cards
  const renderMobileCards = () => (
    <div className="transactions-cards-container">
      {currentTransactions.map((movement) => (
        <div key={movement.id} className="movement-card">
          <div className="card-header">
            <div className="header-left">
              <PlatformText variant="h4" className="challan-number">
                {movement.challanNumber || (movement.type === 'stock_in' ? 'Stock In' : 'Required')}
              </PlatformText>
              <span 
                className="movement-type-badge"
                style={{ backgroundColor: getMovementTypeColor(movement.type) }}
              >
                {getMovementTypeDisplay(movement.type)}
              </span>
            </div>
            <PlatformText variant="caption" className="date-text">
              {formatDate(movement.timestamp)}
            </PlatformText>
          </div>

          <div className="card-content">
            <div className="info-row">
              <PlatformText variant="caption" className="label">Customer:</PlatformText>
              <PlatformText variant="body" className="value">
                {movement.partyName || (movement.type === 'stock_in' ? 'N/A' : 'Required')}
              </PlatformText>
            </div>

            <div className="info-row">
              <PlatformText variant="caption" className="label">Product:</PlatformText>
              <PlatformText variant="body" className="value">
                {movement.productName}
              </PlatformText>
            </div>

            {movement.variantName && (
              <div className="info-row">
                <PlatformText variant="caption" className="label">Variant:</PlatformText>
                <PlatformText variant="body" className="value">
                  {movement.variantName}
                </PlatformText>
              </div>
            )}

            <div className="info-row">
              <PlatformText variant="caption" className="label">SKU:</PlatformText>
              <PlatformText variant="body" className="value">
                {movement.sku || 'N/A'}
              </PlatformText>
            </div>

            <div className="info-row">
              <PlatformText variant="caption" className="label">Quantity:</PlatformText>
              <PlatformText variant="body" className="value">
                {movement.quantity}
              </PlatformText>
            </div>

            <div className="info-row">
              <PlatformText variant="caption" className="label">Stock:</PlatformText>
              <PlatformText variant="body" className="value">
                {movement.previousStock} → {movement.newStock}
              </PlatformText>
            </div>

            {movement.reason && (
              <div className="info-row">
                <PlatformText variant="caption" className="label">Reason:</PlatformText>
                <PlatformText variant="body" className="value">
                  {movement.reason}
                </PlatformText>
              </div>
            )}

            {movement.notes && (
              <div className="info-row">
                <PlatformText variant="caption" className="label">Notes:</PlatformText>
                <PlatformText variant="body" className="value">
                  {movement.notes}
                </PlatformText>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  // Show error if any
  if (error) {
    return (
      <InventoryLayout>
        <div className={`inventory-container ${platformClass}`}>
          <div className="inventory-error">
            <PlatformText variant="h3">Error Loading Transactions</PlatformText>
            <PlatformText variant="body">{error}</PlatformText>
            <PlatformButton onClick={() => window.location.reload()}>Retry</PlatformButton>
          </div>
        </div>
      </InventoryLayout>
    );
  }

  // Show loading
  if (loading) {
    return (
      <InventoryLayout>
        <div className={`inventory-container ${platformClass}`}>
          <div className="inventory-loading">
            <PlatformText variant="h3">Loading transactions...</PlatformText>
          </div>
        </div>
      </InventoryLayout>
    );
  }

  return (
    <InventoryLayout>
      <div className={`inventory-container ${platformClass}`}>
        {/* Mobile App Bar */}
        {isMobilePlatform && (
          <div className="mobile-app-bar inventory-mobile-app-bar">
            <InventoryBackButton onClick={() => navigate('/inventory')}>Transactions</InventoryBackButton>
          </div>
        )}

        {/* Desktop Header */}
        {!isMobilePlatform && (
          <div className="inventory-page-header">
            <div>
              <InventoryBackButton onClick={() => navigate('/inventory')}>Transactions</InventoryBackButton>
              <PlatformText variant="body" className="inventory-page-subtitle">
                View all stock movements and transactions
              </PlatformText>
            </div>
          </div>
        )}

        {/* Filters and Search */}
        <div className="transactions-controls">
          <div className="search-filter-row">
            <div className="search-container">
              <input
                type="text"
                placeholder="Search challan, customer, product, SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="filter-container">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Types</option>
                <option value="stock_in">Stock In</option>
                <option value="stock_out">Stock Out</option>
                <option value="adjustment">Adjustment</option>
              </select>
            </div>

            <div className="filter-container">
              <select
                value={productFilter}
                onChange={(e) => setProductFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Products</option>
                {products.map(product => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="results-info">
            <PlatformText variant="caption" className="results-text">
              Showing {filteredMovements?.length > 0 ? `${startIndex + 1}-${Math.min(endIndex, filteredMovements.length)}` : '0'} of {filteredMovements?.length || 0} transactions
              {productFilter !== 'all' && ` for ${getProductName(productFilter)}`}
              {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
            </PlatformText>
          </div>
        </div>

        {/* Transactions Content */}
        <div className="transactions-content">
          {!filteredMovements || filteredMovements.length === 0 ? (
            <div className="no-transactions">
              <PlatformText variant="h4">No transactions found</PlatformText>
              <PlatformText variant="body">
                {searchTerm || typeFilter !== 'all' || productFilter !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Stock movements will appear here once you start managing inventory'
                }
              </PlatformText>
            </div>
          ) : (
            isMobilePlatform ? renderMobileCards() : renderDesktopTable()
          )}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && filteredMovements && filteredMovements.length > 0 && (
          <div className="pagination-controls">
            <div className="pagination-info">
              <PlatformText variant="caption">
                Page {currentPage} of {totalPages}
              </PlatformText>
            </div>
            
            <div className="pagination-buttons">
              <PlatformButton
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="pagination-btn prev-btn"
              >
                Previous
              </PlatformButton>
              
              <div className="page-numbers">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`page-number ${pageNum === currentPage ? 'active' : ''}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <PlatformButton
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="pagination-btn next-btn"
              >
                Next
              </PlatformButton>
            </div>
          </div>
        )}
      </div>
    </InventoryLayout>
  );
};

export default TransactionsPage; 